import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchOrder } from './api'
import { orderStatusWording } from './constants'
import { useTrackerRuntime } from './runtime'
import { Fulfillment, OrderDataInterface } from './types'
import attachAtlas from './atlas'

export function useOrderStatus(fulfillment?: Fulfillment) {
  const [orderStatus, setOrderStatus] = useState('')
  const [iconColor, setIconColors] = useState(0)

  useEffect(() => {
    const pickupIsInPast =
      fulfillment?.runner_est_pickup_at &&
      new Date(fulfillment.runner_est_pickup_at) < new Date()

    if (fulfillment?.status === 'pending_vendor' && !pickupIsInPast) {
      setOrderStatus(orderStatusWording.CONFIRMING)
    }

    const pendingAssignStatuses = [
      'pending_dispatch',
      'staging',
      'pending_reassign',
    ]

    if (
      fulfillment?.status &&
      pendingAssignStatuses.includes(fulfillment.status)
    ) {
      setOrderStatus(orderStatusWording.FINDING_DRIVER)
      setIconColors(-1)
      return
    }

    if (fulfillment?.runner_assigned_at) {
      setOrderStatus(orderStatusWording.EN_ROUTE_TO_PICK_UP)
      setIconColors(0)
    }
    if (fulfillment?.runner_arrived_pickup) {
      setOrderStatus(orderStatusWording.ARRIVED_AT_PICKED)
      setIconColors(1)
    }
    if (fulfillment?.runner_picked_up) {
      setOrderStatus(orderStatusWording.PICKED_UP)
      setIconColors(2)
    }
    if (fulfillment?.runner_arrived_dropoff) {
      setOrderStatus(orderStatusWording.ARRIVED_DROPPED_OFF)
      setIconColors(3)
    }
    if (
      fulfillment?.runner_dropped_off ||
      fulfillment?.status === 'completed'
    ) {
      setOrderStatus(orderStatusWording.COMPLETED)
    }
    if (fulfillment?.status === 'cancelled') {
      setOrderStatus('Cancelled')
    }
  }, [fulfillment])

  return { orderStatus, iconColor }
}

export function useTrackerData(
  orderId: string,
  apiBaseUrl: string,
  enabled = true
) {
  return useQuery<OrderDataInterface, Error>({
    queryKey: ['tracker-ui', apiBaseUrl, orderId],
    queryFn: () => fetchOrder(orderId, apiBaseUrl),
    staleTime: 5000,
    refetchInterval: 10000,
    enabled: Boolean(enabled && orderId && apiBaseUrl),
  })
}

export const useAtlasTracking = (
  data?: OrderDataInterface,
  atlasKey?: string
) => {
  useEffect(() => {
    const win = window as Window & { Atlas?: any }
    const resolvedKey = atlasKey || data?.operator?.atlas

    if (!data) {
      return undefined
    }

    if (!win.Atlas) {
      attachAtlas(resolvedKey)
    }

    if (data.operator?.customer_support_tracking) {
      win.Atlas?.start({
        recording: {
          enabled: false,
        },
      })

      win.Atlas?.identify({
        userId: data.order?.id || data.customer?.address,
        name: data.customer?.name,
        email: data.merchant?.name,
        merchant_name: data.merchant?.name,
        customFields: {
          operator: data.delivery_service?.name,
          entity: 'diner',
          territory: data.merchant?.territory,
          order_id: data.order?.id,
          merchant_id: data.merchant?.id,
          operator_id: data.operator?.id,
        },
      })
    }

    return undefined
  }, [atlasKey, data])
}

export const useOrderData = (orderId: string) => {
  const runtime = useTrackerRuntime()
  const resolvedOrderId = orderId || runtime.orderId || ''
  const query = useTrackerData(
    resolvedOrderId,
    runtime.apiBaseUrl || '',
    !runtime.data
  )
  const data = runtime.data || query.data

  const { orderStatus, iconColor } = useOrderStatus(data?.fulfillment)
  const timelineEvents = useCreateOrderTimeline(data?.fulfillment)
  const fulfillment = data?.fulfillment
  const delivery_service = data?.delivery_service
  const merchant = data?.merchant
  const runner = data?.runner
  const order = data?.order
  const customer = data?.customer
  const primaryColor = delivery_service?.colors?.primary || '#1F5EA9'
  const containerRef = useRef<HTMLDivElement | null>(null)

  useAtlasTracking(data, runtime.atlasKey)

  return {
    data,
    isLoading: runtime.data ? false : query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    orderStatus,
    customer,
    iconColor,
    timelineEvents,
    fulfillment,
    delivery_service,
    merchant,
    runner,
    order,
    primaryColor,
    fallbackColor: '#1F5EA9',
    container: containerRef,
  }
}

export function useCreateOrderTimeline(fulfillment?: Fulfillment) {
  if (!fulfillment) {
    return []
  }

  const getStatusTime = (options: string[]) => {
    for (const option of options) {
      const candidate = fulfillment[option]
      if (candidate) {
        return candidate
      }
    }
    return ''
  }

  const timelineEvents = [
    {
      statusText: 'Order Placed',
      keys: ['created_at', 'pending_assign'],
    },
    {
      statusText: 'En Route to Pickup',
      keys: ['runner_assigned_at', 'en_route_pickup'],
    },
    {
      statusText: 'Arrived at Pickup',
      keys: ['runner_arrived_pickup', 'arrived_pickup'],
    },
    {
      statusText: 'En Route to You',
      keys: ['runner_picked_up', 'picked_up'],
    },
    {
      statusText: 'Arriving Now',
      keys: ['runner_arrived_dropoff', 'arrived_dropoff'],
    },
    {
      statusText: 'Delivered',
      keys: ['runner_dropped_off', 'completed'],
    },
  ]

  if (fulfillment.status === 'cancelled') {
    timelineEvents.push({
      statusText: 'Cancelled',
      keys: ['cancelled'],
    })
  }

  return timelineEvents
    .map(event => ({
      statusText: event.statusText,
      statusTime: getStatusTime(event.keys),
    }))
    .filter(
      event => event.statusTime || event.statusText === 'Cancelled'
    )
}
